import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Heart, MessageSquare, Share2, Trophy, MapPin, Clock, Zap, Send, Trash2, Edit2, X, Check, MoreHorizontal } from 'lucide-react';
import { Post, UserProfile, Comment } from '../../types';
import { doc, getDoc, updateDoc, arrayUnion, arrayRemove, deleteDoc, addDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { db, getCollectionPath } from '../../lib/firebase';
import { MentionInput } from './MentionInput';
import { getCategoryStyles } from '../../lib/categoryStyles';
import { FadeInImage } from '../common/FadeInImage';

interface PostCardProps {
  key?: React.Key;
  post: Post;
  primaryColor: string;
  userProfile: UserProfile;
  isFullPlus?: boolean;
  onContactClick?: (playerId: string, playerName: string, playerPhoto?: string) => void;
  onPlayerClick?: (playerId: string | null) => void;
  onSedeClick?: (sedeId: string) => void;
  config?: any;
  selectedCity?: string;
}

export function PostCard({ post, primaryColor, userProfile: _userProfile, isFullPlus, onContactClick, onPlayerClick, onSedeClick, config, selectedCity }: PostCardProps) {
  const userProfile = _userProfile || {} as UserProfile;
  const [isLiking, setIsLiking] = useState(false);
  const [isEditingPost, setIsEditingPost] = useState(false);
  const [editingPostContent, setEditingPostContent] = useState('');
  const [postMenuOpen, setPostMenuOpen] = useState(false);
  const [isCommenting, setIsCommenting] = useState(false);
  const [visibleCommentsCount, setVisibleCommentsCount] = useState(1);
  const [newComment, setNewComment] = useState('');
  const [isContacting, setIsContacting] = useState(false);
  const [editingCommentId, setEditingCommentId] = useState<string | null>(null);
  const [editingCommentContent, setEditingCommentContent] = useState('');
  const [commentMenuOpenId, setCommentMenuOpenId] = useState<string | null>(null);
  const [replyingTo, setReplyingTo] = useState<{commentId: string, authorName: string} | null>(null);
  const [showLikersFor, setShowLikersFor] = useState<string[] | null>(null);
  const [likersData, setLikersData] = useState<{name: string, uid: string}[]>([]);
  const [loadingLikers, setLoadingLikers] = useState(false);

  const fetchLikersData = async (uids: string[]) => {
    setShowLikersFor(uids);
    setLoadingLikers(true);
    try {
      const data = await Promise.all(uids.slice(0, 50).map(async uid => {
        let docSnap = await getDoc(doc(db, 'players', uid));
        if (docSnap.exists()) return { uid, name: docSnap.data().name || 'Usuario' };
        docSnap = await getDoc(doc(db, 'users', uid));
        if (docSnap.exists()) return { uid, name: docSnap.data().name || 'Usuario' };
        return { uid, name: 'Usuario' };
      }));
      setLikersData(data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoadingLikers(false);
    }
  };

  const isCurrentUserSuperAdmin = userProfile.role === 'superadmin' || userProfile.uid === 'agunavarro1989@gmail.com' || userProfile.email === 'agunavarro1989@gmail.com';
  const isSuperAdminAuthor = post.authorRole === 'superadmin' || post.authorId === 'agunavarro1989@gmail.com' || (post.authorRole === 'club' && post.authorName?.toLowerCase().includes('admin'));

  const authorPhoto = isSuperAdminAuthor 
    ? (config?.superAdminPhotoUrl || config?.logoUrl || post.authorPhotoUrl)
    : post.authorPhotoUrl;
  
  const authorName = isSuperAdminAuthor
    ? (config?.superAdminName || 'Super Admin')
    : post.authorName;

  const displayRole = isSuperAdminAuthor ? 'superadmin' : post.authorRole;

  const handleContact = async () => {
    const isAdminOrClub = displayRole === 'admin' || displayRole === 'superadmin' || displayRole === 'club';
    if (isAdminOrClub && onContactClick) {
      onContactClick(post.authorId, authorName || 'Usuario RPT', authorPhoto);
      return;
    }

    if (isContacting) return;
    setIsContacting(true);
    try {
      const playerDoc = await getDoc(doc(db, 'players', post.authorId));
      if (playerDoc.exists()) {
        const playerData = playerDoc.data();
        if (playerData.phone) {
          const cleanPhone = playerData.phone.replace(/[^0-9]/g, '');
          const message = encodeURIComponent(`¡Hola ${playerData.name}! Vi tu publicación en la Comunidad RPT buscando jugador ("${post.content.substring(0, 60)}...") y me interesa sumarme.`);
          window.open(`https://wa.me/${cleanPhone}?text=${message}`, '_blank');
          return;
        }
      }
      
      const userDoc = await getDoc(doc(db, 'users', post.authorId));
      if (userDoc.exists()) {
        const userData = userDoc.data();
        if (userData.phone) {
          const cleanPhone = userData.phone.replace(/[^0-9]/g, '');
          const message = encodeURIComponent(`¡Hola ${userData.name || ''}! Vi tu publicación en la Comunidad RPT buscando jugador ("${post.content.substring(0, 60)}...") y me interesa sumarme.`);
          window.open(`https://wa.me/${cleanPhone}?text=${message}`, '_blank');
          return;
        }
      }

      alert('El usuario no tiene un teléfono de contacto configurado en su perfil.');
    } catch (error) {
      console.error('Error fetching contact info:', error);
      alert('No se pudo obtener la información de contacto.');
    } finally {
      setIsContacting(false);
    }
  };

  const isLiked = post.likes.includes(userProfile.uid);

  const canDelete = 
    userProfile.uid === post.authorId || 
    (userProfile.clubId && userProfile.clubId === post.authorId) || 
    userProfile.role === 'superadmin';

  const handleDelete = async () => {
    if (!await window.confirm('¿Estás seguro de que deseas eliminar esta publicación?')) return;
    try {
      await deleteDoc(doc(db, 'posts', post.id));
    } catch (err) {
      console.error('Error al eliminar post:', err);
      alert('No se pudo eliminar la publicación.');
    }
  };

  const submitEditPost = async () => {
    if (!editingPostContent.trim()) return;
    try {
      const postRef = doc(db, 'posts', post.id);
      await updateDoc(postRef, {
        content: editingPostContent.trim()
      });
      setIsEditingPost(false);
      setPostMenuOpen(false);
    } catch (error) {
      console.error('Error al editar publicacion:', error);
      alert('Error al editar publicacion');
    }
  };

  const handleLike = () => {
    if (isLiking) return;
    setIsLiking(true);
    setTimeout(() => setIsLiking(false), 300); // Only disable briefly to prevent double clicks, let UI respond instantly

    try {
      const postRef = doc(db, 'posts', post.id);
      if (isLiked) {
        updateDoc(postRef, {
          likes: arrayRemove(userProfile.uid)
        }).catch(console.error);
      } else {
        updateDoc(postRef, {
          likes: arrayUnion(userProfile.uid)
        }).catch(console.error);
      }
    } catch (error) {
      console.error('Error toggling like:', error);
    }
  };

  const handleComment = async () => {
    if (!newComment.trim() || isCommenting) return;
    setIsCommenting(true);
    setTimeout(() => setIsCommenting(false), 300); // Enable quickly for rapid commenting

    try {
      const postRef = doc(db, 'posts', post.id);
      
      let finalContent = newComment.trim();
      if (replyingTo) {
        const prepopulated = `@${replyingTo.authorName.replace(/\s+/g, '_')}`;
        if (finalContent.startsWith(prepopulated)) {
          finalContent = finalContent.substring(prepopulated.length).trim();
        }
      }

      const commentObj: Comment = {
        id: Date.now().toString() + Math.random().toString(36).substring(2, 9),
        authorId: userProfile.uid,
        authorName: userProfile.name || 'Usuario',
        authorPhotoUrl: userProfile.photoUrl || '',
        content: finalContent,
        createdAt: Date.now(),
      };
      
      if (replyingTo) {
        const updatedComments = post.comments.map(c => {
          if (c.id === replyingTo.commentId) {
            return {
              ...c,
              replies: [...(c.replies || []), commentObj]
            };
          }
          return c;
        });
        updateDoc(postRef, { comments: updatedComments }).catch(console.error);

        // Notify comment author (async)
        const parentComment = post.comments.find(c => c.id === replyingTo.commentId);
        if (parentComment && parentComment.authorId !== userProfile.uid) {
          addDoc(collection(db, getCollectionPath('notifications')), {
            userId: parentComment.authorId,
            type: 'comment_reply',
            title: 'Nueva respuesta',
            body: `${userProfile.name} respondió a tu comentario.`,
            postId: post.id,
            read: false,
            createdAt: Date.now()
          }).catch(console.error);
        }
        setReplyingTo(null);
      } else {
        updateDoc(postRef, {
          comments: arrayUnion(commentObj)
        }).catch(console.error);

        // Notify post author (async)
        if (post.authorId !== userProfile.uid) {
          addDoc(collection(db, getCollectionPath('notifications')), {
            userId: post.authorId,
            type: 'post_comment',
            title: 'Nuevo comentario',
            body: `${userProfile.name} comentó tu publicación.`,
            postId: post.id,
            read: false,
            createdAt: Date.now()
          }).catch(console.error);
        }
      }

      setNewComment('');

      // Notify mentioned users (async)
      const mentions = finalContent.match(/(?:^|\s)(@[^\s]+)/g);
      if (mentions) {
        const processedNames = new Set<string>();
        for (const m of mentions) {
          const mentionName = m.trim().substring(1).replace(/_/g, ' ');
          if (processedNames.has(mentionName)) continue;
          processedNames.add(mentionName);
          
          const userQuery = query(collection(db, 'users'), where('name', '==', mentionName));
          const snaps = await getDocs(userQuery);
          snaps.forEach((docSnap) => {
            if (docSnap.id !== userProfile.uid) {
              addDoc(collection(db, getCollectionPath('notifications')), {
                userId: docSnap.id,
                type: 'comment_mention',
                title: 'Te han mencionado',
                body: `${userProfile.name} te mencionó en un comentario.`,
                postId: post.id,
                read: false,
                createdAt: Date.now()
              }).catch(console.error);
            }
          });
        }
      }
    } catch (error) {
      console.error('Error adding comment:', error);
    }
  };

  const handleDeleteComment = async (commentId: string, parentId?: string) => {
    if (!await window.confirm('¿Eliminar este comentario?')) return;
    try {
      const postRef = doc(db, 'posts', post.id);
      let updatedComments;
      if (parentId) {
        updatedComments = post.comments.map(c => {
          if (c.id === parentId && c.replies) {
            return { ...c, replies: c.replies.filter(r => r.id !== commentId) };
          }
          return c;
        });
      } else {
        updatedComments = post.comments.filter(c => c.id !== commentId);
      }
      await updateDoc(postRef, { comments: updatedComments });
    } catch (error) {
      console.error('Error deleting comment:', error);
    }
  };

  const submitEditComment = async (commentId: string, parentId?: string) => {
    if (!editingCommentContent.trim()) return;
    try {
      const postRef = doc(db, 'posts', post.id);
      let updatedComments;
      if (parentId) {
        updatedComments = post.comments.map(c => {
          if (c.id === parentId && c.replies) {
            return {
              ...c,
              replies: c.replies.map(r => r.id === commentId ? { ...r, content: editingCommentContent.trim() } : r)
            };
          }
          return c;
        });
      } else {
        updatedComments = post.comments.map(c => 
          c.id === commentId ? { ...c, content: editingCommentContent.trim() } : c
        );
      }
      await updateDoc(postRef, { comments: updatedComments });
      setEditingCommentId(null);
    } catch (error) {
      console.error('Error editing comment:', error);
    }
  };

  const handleLikeComment = async (commentId: string, parentId?: string) => {
    try {
      const postRef = doc(db, 'posts', post.id);
      let updatedComments;
      if (parentId) {
        updatedComments = post.comments.map(c => {
          if (c.id === parentId && c.replies) {
            return {
              ...c,
              replies: c.replies.map(r => {
                if (r.id === commentId) {
                  const likes = r.likes || [];
                  const hasLiked = likes.includes(userProfile.uid);
                  return {
                    ...r,
                    likes: hasLiked ? likes.filter(id => id !== userProfile.uid) : [...likes, userProfile.uid]
                  };
                }
                return r;
              })
            };
          }
          return c;
        });
      } else {
        updatedComments = post.comments.map(c => {
          if (c.id === commentId) {
            const likes = c.likes || [];
            const hasLiked = likes.includes(userProfile.uid);
            return {
              ...c,
              likes: hasLiked ? likes.filter(id => id !== userProfile.uid) : [...likes, userProfile.uid]
            };
          }
          return c;
        });
      }
      updateDoc(postRef, { comments: updatedComments }).catch(console.error);
    } catch (error) {
      console.error('Error toggling comment like:', error);
    }
  };

  const fallbackCopy = () => {
    try {
      if (navigator.clipboard && window.isSecureContext) {
        navigator.clipboard.writeText(window.location.href);
        alert('¡Enlace copiado al portapapeles!');
      } else {
        const textArea = document.createElement("textarea");
        textArea.value = window.location.href;
        textArea.style.position = "fixed";
        textArea.style.left = "-999999px";
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        try {
          document.execCommand('copy');
          alert('¡Enlace copiado al portapapeles!');
        } catch (err) {
          console.error('Fallback copy failed', err);
          alert('No se pudo copiar automáticamente. Copia este enlace manualmente:\n\n' + window.location.href);
        }
        document.body.removeChild(textArea);
      }
    } catch (e) {
      alert('No se pudo copiar automáticamente. Copia este enlace manualmente:\n\n' + window.location.href);
    }
  };

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: `Publicación en RPT`,
          text: post.content.substring(0, 100) + '...',
          url: window.location.href,
        });
      } catch (error: any) {
        if (error.name !== 'AbortError') {
          fallbackCopy();
        }
      }
        } else {
      fallbackCopy();
    }
  };

  const renderVideoPlayer = (url: string) => {
    try {
      if (url.includes('youtube.com') || url.includes('youtu.be')) {
        let videoId = '';
        if (url.includes('youtu.be/')) {
          videoId = url.split('youtu.be/')[1]?.split('?')[0];
        } else {
          videoId = new URL(url).searchParams.get('v') || '';
        }
        if (videoId) {
          return <iframe src={`https://www.youtube.com/embed/${videoId}`} className="w-full aspect-video rounded-xl border border-white/10" allowFullScreen></iframe>;
        }
      }
      return (
        <div className="p-4 bg-white/5 rounded-xl flex items-center justify-between border border-white/10">
          <span className="text-sm text-white/60 truncate mr-2">{url}</span>
          <a href={url} target="_blank" rel="noreferrer" className="text-xs bg-white/10 hover:bg-white/20 text-white px-4 py-1.5 rounded-full font-bold shrink-0 transition-colors">Abrir</a>
        </div>
      );
    } catch (e) {
      return null;
    }
  };

  const handleReplyClick = (parentId: string, authorName: string) => {
    setReplyingTo({ commentId: parentId, authorName });
    setNewComment(`@${authorName.replace(/\s+/g, '_')} `);
    setTimeout(() => {
      const input = document.getElementById(`comment-input-${post.id}`) as HTMLInputElement | HTMLTextAreaElement;
      if (input) {
        input.focus();
        const length = input.value.length;
        input.setSelectionRange(length, length);
      }
    }, 50);
  };

  const isTournament = post.type === 'tournament';
  const isMatchmaking = post.type === 'matchmaking';
  const isAnnouncement = post.type === 'announcement';

  const renderTextWithMentions = (text: string) => {
    if (!text) return null;
    const parts = text.split(/(^|\s)(@[^\s]+)/);
    return parts.map((part, i) => {
      if (part && part.startsWith('@')) {
        const name = part.substring(1).replace(/_/g, ' ');
        return (
          <span key={i} className="font-bold text-theme-primary cursor-pointer hover:underline transition-colors">
            @{name}
          </span>
        );
      }
      return <React.Fragment key={i}>{part}</React.Fragment>;
    });
  };

  return (
    <motion.div
      id={`post-${post.id}`}
      initial={{ opacity: 0, y: 15 }}
      animate={{ opacity: 1, y: 0 }}
      className={`w-full rounded-[1.5rem] border backdrop-blur-md overflow-hidden relative ${
        isTournament 
          ? 'bg-gradient-to-b from-amber-500/10 to-slate-900/80 border-amber-500/30 shadow-[0_5px_30px_rgba(245,158,11,0.1)]'
          : isMatchmaking
            ? 'bg-slate-900/80 border-theme-primary/30'
            : isAnnouncement
              ? 'bg-gradient-to-br from-slate-800 to-slate-900 border-white/20'
              : isFullPlus
                ? 'bg-slate-900/80 border-theme-primary/50 shadow-[0_0_20px_rgba(var(--theme-primary-rgb),0.15)]'
                : 'bg-slate-900/60 border-white/10'
      }`}
    >
      {isTournament && (
        <div className="w-full bg-amber-500/20 py-2 px-5 flex items-center justify-between border-b border-amber-500/30">
          <div className="flex items-center gap-2 text-amber-400">
            <Trophy size={14} className="animate-pulse" />
            <span className="text-[10px] font-black uppercase tracking-widest">Torneo Oficial</span>
          </div>
          <span className="text-[9px] font-bold text-amber-400/60 uppercase">Inscripciones Abiertas</span>
        </div>
      )}

      {isMatchmaking && (
        <div className="w-full bg-theme-primary/10 py-1.5 px-5 flex items-center justify-between border-b border-theme-primary/20">
          <div className="flex items-center gap-2 theme-primary-text">
            <Zap size={14} />
            <span className="text-[10px] font-black uppercase tracking-widest">Buscando Jugador</span>
          </div>
        </div>
      )}

      <div className="p-5 md:p-6">
        <div className="flex justify-between items-start mb-4 gap-2">
          <div 
            onClick={() => {
              if (displayRole === 'superadmin') return;
              if (displayRole === 'admin') {
                onSedeClick?.(post.authorId);
              } else {
                onPlayerClick?.(post.authorId);
              }
            }}
            className={`flex items-center gap-3 min-w-0 flex-1 ${(onPlayerClick || onSedeClick) && displayRole !== 'superadmin' ? 'cursor-pointer group/author' : ''}`}
          >
            <div className="w-10 h-10 md:w-12 md:h-12 rounded-full bg-white/10 shrink-0 overflow-hidden border border-white/20 group-hover/author:border-white/40 transition-colors">
              {authorPhoto ? (
                <FadeInImage src={authorPhoto} alt={authorName} className="w-full h-full object-cover group-hover/author:scale-105 transition-transform" />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-white/40 font-bold bg-white/5">
                  {authorName?.charAt(0) || 'U'}
                </div>
              )}
            </div>
            <div className="flex flex-col min-w-0 flex-1">
              <span className="text-sm md:text-base font-bold text-white flex items-center gap-2 group-hover/author:text-theme-primary/90 transition-colors">
                <span className="truncate">{authorName}</span>
                {(displayRole === 'admin' || displayRole === 'superadmin') && (
                  <span className={`rounded font-black uppercase tracking-wider shrink-0 ${
                    displayRole === 'superadmin' 
                      ? 'border border-yellow-400 bg-yellow-400/10 text-yellow-400 text-[7px] px-1 py-0.5 shadow-[0_0_6px_rgba(250,204,21,0.2)]' 
                      : 'bg-theme-primary/20 text-theme-primary text-[8px] px-1.5 py-0.5'
                  }`}>
                    {displayRole === 'superadmin' ? 'Oficial' : 'Club'}
                  </span>
                )}
                {isFullPlus && !isTournament && !isMatchmaking && (
                  <span className="px-1.5 py-0.5 rounded text-[8px] font-black uppercase bg-amber-500/20 text-amber-400 flex items-center gap-1">
                    <Trophy size={8} /> Destacado
                  </span>
                )}
              </span>
              <span className="text-[10px] text-white/40 uppercase tracking-widest mt-0.5">
                {new Date(post.createdAt).toLocaleDateString()} a las {new Date(post.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
              </span>
            </div>
          </div>
          {canDelete && (
            <div className="relative flex items-center shrink-0">
              <button 
                onClick={() => setPostMenuOpen(!postMenuOpen)}
                className="p-2 bg-black/20 text-white/40 hover:text-white hover:bg-white/10 rounded-xl transition-all"
                title="Opciones de publicación"
              >
                <MoreHorizontal size={16} />
              </button>
              {postMenuOpen && (
                <>
                  <div className="fixed inset-0 z-10" onClick={() => setPostMenuOpen(false)} />
                  <div className="absolute right-0 top-full mt-1 w-32 bg-slate-800 border border-white/10 rounded-xl shadow-xl overflow-hidden z-20">
                    {(userProfile.uid === post.authorId || userProfile.role === 'superadmin') && (
                      <button 
                        onClick={() => { setIsEditingPost(true); setEditingPostContent(post.content); setPostMenuOpen(false); }}
                        className="w-full px-3 py-2 text-xs text-left text-white/80 hover:bg-white/5 hover:text-white flex items-center gap-2 transition-colors"
                      >
                        <Edit2 size={12} /> Editar
                      </button>
                    )}
                    <button 
                      onClick={() => { handleDelete(); setPostMenuOpen(false); }}
                      className="w-full px-3 py-2 text-xs text-left text-red-400 hover:bg-red-500/10 hover:text-red-300 flex items-center gap-2 transition-colors border-t border-white/5"
                    >
                      <Trash2 size={12} /> Eliminar
                    </button>
                  </div>
                </>
              )}
            </div>
          )}
        </div>

        {isEditingPost ? (
          <div className="flex flex-col gap-2 mb-4">
            <MentionInput
              isTextArea
              value={editingPostContent}
              onChange={setEditingPostContent}
              className="w-full bg-black/40 border border-white/20 rounded-xl px-3 py-2 text-sm text-white focus:outline-none focus:border-theme-primary/50 min-h-[80px]"
            />
            <div className="flex justify-end gap-2">
              <button onClick={() => setIsEditingPost(false)} className="px-3 py-1.5 text-xs text-white/60 hover:text-white bg-white/5 hover:bg-white/10 rounded-lg transition-colors">Cancelar</button>
              <button onClick={submitEditPost} style={{ backgroundColor: primaryColor }} className="px-3 py-1.5 text-xs text-black font-bold hover:brightness-110 rounded-lg transition-colors flex items-center gap-1">Guardar <Check size={12} /></button>
            </div>
          </div>
        ) : (
          <div className="text-sm font-medium text-white/90 mb-4 break-words leading-relaxed whitespace-pre-wrap">
            {renderTextWithMentions(post.content)}
          </div>
        )}

        {post.imagesUrl && post.imagesUrl.length > 0 && (
          <div className="mb-4 -mx-1">
            {post.imagesUrl.length === 1 ? (
              <FadeInImage src={post.imagesUrl[0]} alt="Post" containerClassName="w-full rounded-xl border border-white/10" className="h-auto max-h-[500px] rounded-xl" />
            ) : (
              <div className="flex overflow-x-auto snap-x snap-mandatory scrollbar-hide gap-2 px-1">
                {post.imagesUrl.map((url, idx) => (
                  <FadeInImage key={idx} src={url} alt={`Post ${idx + 1}`} containerClassName="w-4/5 shrink-0 snap-center h-[350px] rounded-xl border border-white/10" className="rounded-xl" />
                ))}
              </div>
            )}
          </div>
        )}

        {post.videoUrl && (
          <div className="mb-4">
            {renderVideoPlayer(post.videoUrl)}
          </div>
        )}

        {(post.targetCategories?.length || post.targetGender) && (
          <div className="flex flex-wrap gap-2 mb-5">
            {post.targetGender && (
              <span className="px-2.5 py-1 rounded border border-white/10 bg-white/5 text-[10px] font-bold text-white/60">
                {post.targetGender === 'M' || post.targetGender === 'Masculino' ? 'Masculino' : post.targetGender === 'F' || post.targetGender === 'Femenino' ? 'Femenino' : 'Mixto'}
              </span>
            )}
            {post.targetCategories?.map(cat => {
              const style = getCategoryStyles(cat);
              return (
                <span key={cat} className={`px-2.5 py-1 rounded border text-[10px] font-bold uppercase ${style.border} ${style.bg} ${style.text}`}>
                  {cat}
                </span>
              );
            })}
          </div>
        )}

        {isTournament && (
          <button className="w-full py-3 rounded-xl bg-amber-500 text-black font-black uppercase tracking-widest text-xs hover:bg-amber-400 transition-colors mb-4 flex items-center justify-center gap-2 shadow-[0_0_20px_rgba(245,158,11,0.3)]">
            Ver Torneo <Trophy size={14} />
          </button>
        )}
        {isMatchmaking && (
          <button 
            onClick={handleContact}
            disabled={isContacting}
            style={{ backgroundColor: primaryColor }}
            className="w-full py-2.5 rounded-xl text-black font-black uppercase tracking-widest text-[11px] transition-all mb-4 flex items-center justify-center gap-2 hover:brightness-110 active:scale-98 shadow-[0_0_20px_rgba(var(--glow-rgb),0.15)] disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isContacting ? 'Obteniendo contacto...' : 'Mensaje Directo'}
          </button>
        )}

        <div className="flex items-center gap-4 pt-4 border-t border-white/10">
          <div className="flex items-center gap-1.5">
            <motion.button 
              whileTap={{ scale: 0.8 }}
              onClick={handleLike}
              disabled={isLiking}
              className={`flex items-center transition-colors group ${isLiked ? 'text-rose-500' : 'text-white/40 hover:text-rose-400'}`}
            >
              <motion.div
                animate={isLiked ? { scale: [1, 1.4, 1], rotate: [0, -10, 10, 0] } : { scale: 1, rotate: 0 }}
                transition={{ duration: 0.4, ease: "easeInOut" }}
              >
                <Heart size={18} className={isLiked ? 'fill-current' : ''} /> 
              </motion.div>
            </motion.button>
            <span 
              className={`text-xs font-bold cursor-pointer transition-colors ${post.likes.length > 0 ? 'text-white/80 hover:text-white hover:underline' : 'text-white/40'}`}
              onClick={() => post.likes.length > 0 && fetchLikersData(post.likes)}
            >
              {post.likes.length}
            </span>
          </div>

          <button 
            onClick={() => document.getElementById(`comment-input-${post.id}`)?.focus()}
            className="flex items-center gap-1.5 transition-colors text-white/40 hover:text-white"
          >
            <MessageSquare size={18} /> 
            <span className="text-xs font-bold">{post.comments.length}</span>
          </button>
          <button 
            onClick={handleShare}
            className="flex items-center gap-1.5 text-white/40 hover:text-white transition-colors ml-auto"
          >
            <Share2 size={18} />
          </button>
        </div>

        <div className="pt-4 mt-4 border-t border-white/5 flex flex-col gap-3">
          {post.comments.length > visibleCommentsCount && (
            <button 
              onClick={() => setVisibleCommentsCount(prev => prev + 5)}
              className="text-xs text-white/50 hover:text-white/80 font-bold self-start transition-colors mb-1"
            >
              Ver {Math.min(5, post.comments.length - visibleCommentsCount)} comentarios más
            </button>
          )}
          
          {post.comments.length > 0 ? (
            <AnimatePresence initial={false}>
              {[...post.comments]
                .sort((a, b) => b.createdAt - a.createdAt)
                .slice(0, visibleCommentsCount)
                .map(comment => {
                const isCommentSuperAdmin = comment.authorId === 'agunavarro1989@gmail.com' || comment.authorName?.toLowerCase().includes('admin');
                const commentPhoto = isCommentSuperAdmin ? (config?.superAdminPhotoUrl || config?.logoUrl || comment.authorPhotoUrl) : comment.authorPhotoUrl;
                const commentName = isCommentSuperAdmin ? (config?.superAdminName || 'Super Admin') : comment.authorName;

                return (
                  <motion.div 
                    key={comment.id}
                    layout
                    initial={{ opacity: 0, y: -20, height: 0 }}
                    animate={{ opacity: 1, y: 0, height: 'auto' }}
                    exit={{ opacity: 0, scale: 0.9, height: 0 }}
                    transition={{ duration: 0.3, type: "spring", bounce: 0.3 }}
                    className="flex flex-col gap-0 overflow-hidden"
                  >
                    <div className="flex gap-2 group">
                      <div className="flex flex-col items-center shrink-0 w-7">
                        <div 
                          onClick={() => { if (!isCommentSuperAdmin) onPlayerClick?.(comment.authorId); }}
                          className={`w-7 h-7 rounded-full bg-white/10 shrink-0 overflow-hidden border border-white/10 ${(onPlayerClick && !isCommentSuperAdmin) ? 'cursor-pointer hover:border-white/30 transition-colors group/comment-author' : ''}`}
                        >
                          {commentPhoto ? (
                            <FadeInImage src={commentPhoto} alt="User" className="w-full h-full object-cover group-hover/comment-author:scale-105 transition-transform" />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center text-white/40 text-[10px] font-bold">
                              {commentName.charAt(0)}
                            </div>
                          )}
                        </div>
                        {comment.replies && comment.replies.length > 0 && (
                          <div className="w-px bg-white/10 flex-1 mt-2" />
                        )}
                      </div>
                      <div className="flex flex-col flex-1 min-w-0 relative">
                        <div className="flex flex-col bg-white/5 rounded-2xl rounded-tl-none p-2.5">
                          <div className="flex justify-between items-center mb-1">
                          <span 
                            onClick={() => { if (!isCommentSuperAdmin) onPlayerClick?.(comment.authorId); }}
                            className={`text-xs font-bold text-white/90 truncate mr-2 ${(onPlayerClick && !isCommentSuperAdmin) ? 'cursor-pointer hover:text-theme-primary hover:underline transition-all' : ''}`}
                          >
                            {commentName}
                          </span>
                          <span className="text-[9px] text-white/30 uppercase tracking-widest shrink-0">
                            {new Date(comment.createdAt).toLocaleDateString()}
                          </span>
                        </div>
                        {editingCommentId === comment.id ? (
                          <div className="flex gap-1 items-center mt-1">
                            <input 
                              type="text"
                              value={editingCommentContent}
                              onChange={e => setEditingCommentContent(e.target.value)}
                              onKeyDown={e => { if(e.key === 'Enter') submitEditComment(comment.id); if(e.key === 'Escape') setEditingCommentId(null); }}
                              autoFocus
                              className="flex-1 min-w-0 bg-black/40 border border-white/20 rounded px-2 py-1 text-xs text-white focus:outline-none focus:border-theme-primary/50"
                            />
                            <button onClick={() => submitEditComment(comment.id)} className="p-1.5 text-green-400 hover:text-green-300 transition-colors bg-white/5 hover:bg-white/10 rounded shrink-0" title="Guardar">
                              <Check size={12} />
                            </button>
                            <button onClick={() => setEditingCommentId(null)} className="p-1.5 text-red-400 hover:text-red-300 transition-colors bg-white/5 hover:bg-white/10 rounded shrink-0" title="Cancelar">
                              <X size={12} />
                            </button>
                          </div>
                        ) : (
                          <div className="flex justify-between items-start gap-2">
                            <span className="text-xs text-white/70 break-words flex-1 min-w-0">{renderTextWithMentions(comment.content)}</span>
                            {(comment.authorId === userProfile.uid || isCurrentUserSuperAdmin) && (
                              <div className="relative flex items-center shrink-0">
                                <button 
                                  onClick={() => setCommentMenuOpenId(commentMenuOpenId === comment.id ? null : comment.id)}
                                  className="p-1 text-white/40 hover:text-white transition-colors opacity-100 sm:opacity-0 sm:group-hover:opacity-100"
                                >
                                  <MoreHorizontal size={14} />
                                </button>
                                
                                {commentMenuOpenId === comment.id && (
                                  <>
                                    <div className="fixed inset-0 z-10" onClick={() => setCommentMenuOpenId(null)} />
                                    <div className="absolute right-0 top-full mt-1 w-28 bg-slate-800 border border-white/10 rounded-xl shadow-xl overflow-hidden z-20">
                                      <button 
                                        onClick={() => { setEditingCommentId(comment.id); setEditingCommentContent(comment.content); setCommentMenuOpenId(null); }}
                                        className="w-full px-3 py-2 text-xs text-left text-white/80 hover:bg-white/5 hover:text-white flex items-center gap-2 transition-colors"
                                      >
                                        <Edit2 size={12} /> Editar
                                      </button>
                                      <button 
                                        onClick={() => { handleDeleteComment(comment.id); setCommentMenuOpenId(null); }}
                                        className="w-full px-3 py-2 text-xs text-left text-red-400 hover:bg-red-500/10 hover:text-red-300 flex items-center gap-2 transition-colors border-t border-white/5"
                                      >
                                        <Trash2 size={12} /> Eliminar
                                      </button>
                                    </div>
                                  </>
                                )}
                              </div>
                            )}
                          </div>
                        )}
                      </div>

                        {editingCommentId !== comment.id && (
                          <div className="flex items-center gap-3 mt-1 ml-2 mb-2">
                            <div className="flex items-center gap-1">
                              {comment.likes && comment.likes.length > 0 && (
                                <span 
                                  onClick={() => fetchLikersData(comment.likes!)}
                                  className={`text-[11px] font-bold cursor-pointer hover:underline transition-colors ${comment.likes?.includes(userProfile.uid) ? 'text-rose-500 hover:text-rose-400' : 'text-white/50 hover:text-white'}`}
                                >
                                  {comment.likes.length}
                                </span>
                              )}
                              <button 
                                onClick={() => handleLikeComment(comment.id)}
                                className={`text-[11px] font-bold transition-all ${comment.likes?.includes(userProfile.uid) ? 'text-rose-500 scale-105' : 'text-white/40 hover:text-white/80'}`}
                              >
                                Me gusta
                              </button>
                            </div>

                            <button 
                              onClick={() => handleReplyClick(comment.id, commentName)}
                              className="text-[11px] font-bold text-white/40 hover:text-white/80 transition-colors"
                            >
                              Responder
                            </button>
                          </div>
                        )}
                      </div>
                    </div>

                    {comment.replies && comment.replies.length > 0 && (
                      <div className="ml-8 flex flex-col pt-1">
                        {comment.replies.map((reply, index) => {
                          const isLast = index === comment.replies.length - 1;
                          const isReplySuperAdmin = reply.authorId === 'agunavarro1989@gmail.com' || reply.authorName?.toLowerCase().includes('admin');
                          const replyPhoto = isReplySuperAdmin ? (config?.superAdminPhotoUrl || config?.logoUrl || reply.authorPhotoUrl) : reply.authorPhotoUrl;
                          const replyName = isReplySuperAdmin ? (config?.superAdminName || 'Super Admin') : reply.authorName;

                          return (
                            <div key={reply.id} className="relative flex gap-2 group pb-2">
                              <div className="absolute left-[-18px] top-[10px] w-[18px] h-px bg-white/10" />
                              <div 
                                className="absolute left-[-18px] w-px bg-white/10"
                                style={{
                                  top: index === 0 ? '-4px' : '0',
                                  bottom: isLast ? 'calc(100% - 10px)' : '0'
                                }}
                              />
                              <div 
                                onClick={() => { if (!isReplySuperAdmin) onPlayerClick?.(reply.authorId); }}
                                className={`w-5 h-5 rounded-full bg-white/10 shrink-0 overflow-hidden border border-white/10 z-10 ${(onPlayerClick && !isReplySuperAdmin) ? 'cursor-pointer hover:border-white/30 transition-colors' : ''}`}
                              >
                                {replyPhoto ? (
                                  <FadeInImage src={replyPhoto} alt="User" className="w-full h-full object-cover" />
                                ) : (
                                  <div className="w-full h-full flex items-center justify-center text-white/40 text-[9px] font-bold">
                                    {replyName?.charAt(0) || 'U'}
                                  </div>
                                )}
                              </div>
                              <div className="flex flex-col flex-1 min-w-0 relative">
                                <div className="flex flex-col bg-white/5 rounded-2xl rounded-tl-none p-2">
                                  <div className="flex justify-between items-center mb-0.5">
                                    <span 
                                      onClick={() => { if (!isReplySuperAdmin) onPlayerClick?.(reply.authorId); }}
                                      className={`text-[11px] font-bold text-white/90 truncate mr-2 ${(onPlayerClick && !isReplySuperAdmin) ? 'cursor-pointer hover:text-theme-primary hover:underline transition-all' : ''}`}
                                    >
                                      {replyName}
                                    </span>
                                    <span className="text-[8px] text-white/30 uppercase tracking-widest shrink-0">
                                      {new Date(reply.createdAt).toLocaleDateString()}
                                    </span>
                                  </div>
                                  {editingCommentId === reply.id ? (
                                    <div className="flex gap-1 items-center mt-1">
                                      <input 
                                        type="text"
                                        value={editingCommentContent}
                                        onChange={e => setEditingCommentContent(e.target.value)}
                                        onKeyDown={e => { if(e.key === 'Enter') submitEditComment(reply.id, comment.id); if(e.key === 'Escape') setEditingCommentId(null); }}
                                        autoFocus
                                        className="flex-1 min-w-0 bg-black/40 border border-white/20 rounded px-2 py-1 text-[11px] text-white focus:outline-none focus:border-theme-primary/50"
                                      />
                                      <button onClick={() => submitEditComment(reply.id, comment.id)} className="p-1 text-green-400 hover:text-green-300 transition-colors bg-white/5 hover:bg-white/10 rounded shrink-0" title="Guardar"><Check size={10} /></button>
                                      <button onClick={() => setEditingCommentId(null)} className="p-1 text-red-400 hover:text-red-300 transition-colors bg-white/5 hover:bg-white/10 rounded shrink-0" title="Cancelar"><X size={10} /></button>
                                    </div>
                                  ) : (
                                    <div className="flex justify-between items-start gap-2">
                                      <span className="text-[11px] text-white/70 break-words flex-1 min-w-0">{renderTextWithMentions(reply.content)}</span>
                                      {(reply.authorId === userProfile.uid || isCurrentUserSuperAdmin) && (
                                        <div className="relative flex items-center shrink-0">
                                          <button 
                                            onClick={() => setCommentMenuOpenId(commentMenuOpenId === reply.id ? null : reply.id)}
                                            className="p-0.5 text-white/40 hover:text-white transition-colors opacity-100 sm:opacity-0 sm:group-hover:opacity-100"
                                          >
                                            <MoreHorizontal size={12} />
                                          </button>
                                          {commentMenuOpenId === reply.id && (
                                            <>
                                              <div className="fixed inset-0 z-10" onClick={() => setCommentMenuOpenId(null)} />
                                              <div className="absolute right-0 top-full mt-1 w-28 bg-slate-800 border border-white/10 rounded-xl shadow-xl overflow-hidden z-20">
                                                <button 
                                                  onClick={() => { setEditingCommentId(reply.id); setEditingCommentContent(reply.content); setCommentMenuOpenId(null); }}
                                                  className="w-full px-3 py-2 text-xs text-left text-white/80 hover:bg-white/5 hover:text-white flex items-center gap-2 transition-colors"
                                                ><Edit2 size={12} /> Editar</button>
                                                <button 
                                                  onClick={() => { handleDeleteComment(reply.id, comment.id); setCommentMenuOpenId(null); }}
                                                  className="w-full px-3 py-2 text-xs text-left text-red-400 hover:bg-red-500/10 hover:text-red-300 flex items-center gap-2 transition-colors border-t border-white/5"
                                                ><Trash2 size={12} /> Eliminar</button>
                                              </div>
                                            </>
                                          )}
                                        </div>
                                      )}
                                    </div>
                                  )}
                                </div>
                                {editingCommentId !== reply.id && (
                                  <div className="flex items-center gap-3 mt-1 ml-2 mb-1">
                                    <div className="flex items-center gap-1">
                                      {reply.likes && reply.likes.length > 0 && (
                                        <span onClick={() => fetchLikersData(reply.likes!)} className={`text-[10px] font-bold cursor-pointer hover:underline transition-colors ${reply.likes?.includes(userProfile.uid) ? 'text-rose-500 hover:text-rose-400' : 'text-white/50 hover:text-white'}`}>
                                          {reply.likes.length}
                                        </span>
                                      )}
                                      <button onClick={() => handleLikeComment(reply.id, comment.id)} className={`text-[10px] font-bold transition-all ${reply.likes?.includes(userProfile.uid) ? 'text-rose-500 scale-105' : 'text-white/40 hover:text-white/80'}`}>
                                        Me gusta
                                      </button>
                                    </div>
                                    <button onClick={() => handleReplyClick(comment.id, replyName)} className="text-[10px] font-bold text-white/40 hover:text-white/80 transition-colors">
                                      Responder
                                    </button>
                                  </div>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </motion.div>
                );
              })}
            </AnimatePresence>
          ) : null}

          {visibleCommentsCount > 1 && post.comments.length > 1 && (
            <div className="flex items-center gap-3 text-xs font-bold text-white/50 mt-1 mb-2">
              <button onClick={() => setVisibleCommentsCount(1)} className="hover:text-white/80 transition-colors">
                Ocultar
              </button>
              {visibleCommentsCount < post.comments.length && (
                <>
                  <span className="text-white/20">•</span>
                  <button onClick={() => setVisibleCommentsCount(post.comments.length)} className="hover:text-white/80 transition-colors">
                    Ver todos
                  </button>
                </>
              )}
            </div>
          )}
          
          <div className="flex gap-2 items-center mt-2">
            <div className="w-6 h-6 rounded-full bg-theme-primary/20 flex items-center justify-center border border-theme-primary/30 shrink-0">
              <span className="text-[10px] font-bold theme-primary-text">Tú</span>
            </div>
            <div className="flex-1 flex gap-2 relative">
              <MentionInput 
                id={`comment-input-${post.id}`}
                selectedCity={selectedCity}
                value={newComment}
                onChange={setNewComment}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleComment();
                }}
                placeholder={replyingTo ? `Respondiendo a ${replyingTo.authorName}...` : "Escribe un comentario..."}
                className="w-full bg-white/5 border border-white/10 rounded-full px-4 py-2 text-xs text-white placeholder:text-white/30 focus:outline-none focus:border-theme-primary/50"
              />
              <button 
                onClick={handleComment}
                disabled={!newComment.trim() || isCommenting}
                className="p-2 flex items-center justify-center text-white disabled:opacity-50 disabled:cursor-not-allowed hover:scale-110 transition-transform shrink-0"
              >
                <Send size={16} />
              </button>
            </div>
          </div>
        </div>
      </div>

      <AnimatePresence>
        {showLikersFor && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" onClick={() => setShowLikersFor(null)}>
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              onClick={e => e.stopPropagation()}
              className="w-full max-w-xs bg-slate-900 border border-white/10 rounded-2xl overflow-hidden shadow-2xl flex flex-col max-h-[80vh]"
            >
              <div className="flex items-center justify-between p-4 border-b border-white/10 bg-white/5">
                <div className="flex items-center gap-2">
                  <div className="w-6 h-6 rounded-full bg-rose-500/20 flex items-center justify-center">
                    <Heart size={12} className="fill-rose-500 text-rose-500" />
                  </div>
                  <h3 className="text-sm font-bold text-white">Me gusta</h3>
                </div>
                <button onClick={() => setShowLikersFor(null)} className="p-1 text-white/40 hover:text-white transition-colors">
                  <X size={18} />
                </button>
              </div>
              <div className="p-2 overflow-y-auto flex-1">
                {loadingLikers ? (
                  <div className="flex items-center justify-center py-8">
                    <div className="w-6 h-6 border-2 border-amber-500 border-t-transparent rounded-full animate-spin" />
                  </div>
                ) : (
                  <div className="flex flex-col gap-1">
                    {likersData.map((liker, idx) => (
                      <div key={idx} className="flex items-center gap-3 p-2 hover:bg-white/5 rounded-xl transition-colors cursor-pointer" onClick={() => { setShowLikersFor(null); onPlayerClick?.(liker.uid); }}>
                        <div className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center text-xs font-bold text-white/50">
                          {liker.name.charAt(0)}
                        </div>
                        <span className="text-sm font-semibold text-white/90">{liker.name}</span>
                      </div>
                    ))}
                    {likersData.length === 0 && (
                      <div className="text-center py-4 text-xs text-white/40">
                        Nadie ha dado Me gusta aún.
                      </div>
                    )}
                  </div>
                )}
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}
